import { useEffect, useState, useMemo, useRef } from "react";
import { Money } from "./SaudiRiyal";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Building2, FileText, Wallet, CalendarClock, Search, Sparkles, Paperclip, Loader2, X, ExternalLink, Lock } from "lucide-react";
import { api, fileUrl } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "./ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "./ui/table";
import { StatusIcon, Countdown } from "./DocStatus";
import { useLang } from "../i18n/LanguageContext";
import { WorkPermitsBox, isGosiCat } from "./WorkPermitsBox";
import { CostBoxes, CostByCategory } from "./CompanyCostBoxes";
import { MissingNationality } from "./MissingNationality";
import { RenewalForecast } from "./RenewalForecast";
import { CategoryCostChart } from "./CategoryCostChart";
import { CategoriesManager } from "./CategoriesManager";

const WP_RE = /رخص[ةه]?\s*العمل|كرت\s*العمل|work\s*permit|iqama/i;
const IQ_RE = /^\s*(ال)?[إا]قام[ةه]/;
const kindOf = (name = "", category = "") => isGosiCat(category) ? category : category === "كرت العمل" ? "كرت العمل" : category === "الإقامة" ? "الإقامة"
  : WP_RE.test(name) ? (/iqama/i.test(name) ? "الإقامة" : "كرت العمل") : IQ_RE.test(name) ? "الإقامة" : null;
const NameHint = ({ name, category, saved }) => {
  const kind = kindOf(name, category);
  if (kind) return (
    <p className="mt-1.5 flex items-start gap-1.5 rounded-lg bg-emerald-50 px-2 py-1.5 text-xs text-emerald-800" data-testid="record-name-hint-ok">
      <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0" /> تم التعرّف على «{kind}» — {saved ? "مربع الحساب التلقائي ظاهر بالأسفل." : "بعد الحفظ افتح السجل بالتعديل واضغط «مطابقة» لحساب المبلغ تلقائياً."}
    </p>
  );
  if (/[إا]قام[ةه]|رخص/.test(name || "")) return (
    <p className="mt-1.5 rounded-lg bg-amber-50 px-2 py-1.5 text-xs text-amber-800" data-testid="record-name-hint-warn">
      لن يُفعَّل الحساب التلقائي بهذه الصيغة — اختر النوع «الإقامة» أو «كرت العمل» من قائمة التصنيف.
    </p>
  );
  return null;
};

const fmtMoney = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return "—";
  return <Money value={n} decimals />;
};

const PERIODS = { monthly: "شهر", quarterly: "3 أشهر", semiannual: "6 أشهر", yearly: "سنة" };
const FACTOR = { monthly: 12, quarterly: 4, semiannual: 2, yearly: 1 };
const emptyForm = { name: "", category: "", expiry_date: "", last_payment_amount: "", last_payment_date: "", payment_period: "yearly", notes: "", attachment_id: null, attachment_name: null, cr_number: "", verify_url: "" };

const RecordFormDialog = ({ open, onOpenChange, onSaved, categories, initial, used = [] }) => {
  const { t, dir } = useLang();
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (open) {
      setForm(initial ? {
        name: initial.name || "", category: initial.category || "",
        expiry_date: initial.expiry_date || "",
        last_payment_amount: initial.last_payment_amount ?? "",
        last_payment_date: initial.last_payment_date || "",
        payment_period: initial.payment_period || "yearly",
        notes: initial.notes || "",
        attachment_id: initial.attachment_id || null,
        attachment_name: initial.attachment_name || null,
        cr_number: initial.cr_number || "",
        verify_url: initial.verify_url || "",
      } : emptyForm);
    }
  }, [open, initial]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setExtracting(true);
    let attachmentId = null;
    try {
      // Step 1: fast upload + QR decode (must succeed).
      const res = await api.extractCompanyRecord(file);
      attachmentId = res.attachment_id;
      setForm((f) => ({
        ...f,
        attachment_id: res.attachment_id,
        attachment_name: res.attachment_name,
        verify_url: res.verify_url || f.verify_url,
      }));
      toast.success(res.verify_url ? t("dash.crec.uploadedQr") : t("dash.crec.uploaded"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("dash.crec.uploadFail"));
      setExtracting(false);
      return;
    }
    // Step 2: best-effort AI reading (separate call; failure is non-fatal).
    try {
      const res = await api.readCompanyRecord(attachmentId);
      setForm((f) => ({
        ...f,
        category: (res.category && categories.includes(res.category)) ? res.category : f.category,
        expiry_date: res.expiry_date || f.expiry_date,
        name: f.name.trim() ? f.name : (res.name || res.category || f.name),
        cr_number: res.cr_number || f.cr_number,
      }));
      const found = [];
      if (res.category) found.push(`${t("dash.crec.fType")} ${res.category}`);
      if (res.expiry_date) found.push(`${t("dash.crec.fExpiry")} ${res.expiry_date}`);
      if (res.cr_number) found.push(`${t("dash.crec.fCr")} ${res.cr_number}`);
      if (found.length) toast.success(`${t("dash.crec.extractedPrefix")} (${found.join(" · ")})`);
      else toast.info(t("dash.crec.noAuto"));
    } catch (err) {
      toast.warning(t("dash.crec.readFail"));
    } finally {
      setExtracting(false);
    }
  };

  const submit = async () => {
    if (!form.name.trim()) { toast.warning(t("dash.crec.warnName")); return; }
    setSaving(true);
    const payload = {
      name: form.name.trim(),
      category: form.category || "",
      expiry_date: form.expiry_date || null,
      last_payment_amount: form.last_payment_amount === "" ? null : Number(form.last_payment_amount),
      last_payment_date: form.last_payment_date || null,
      payment_period: form.payment_period || "yearly",
      notes: form.notes || "",
      attachment_id: form.attachment_id,
      attachment_name: form.attachment_name,
      cr_number: form.cr_number || null,
      verify_url: form.verify_url || null,
    };
    try {
      if (initial) {
        await api.updateCompanyRecord(initial.id, payload);
        toast.success(t("dash.crec.updated"));
      } else {
        await api.createCompanyRecord(payload);
        toast.success(t("dash.crec.added"));
      }
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("dash.crec.saveFail"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent dir={dir} className="w-[95vw] max-w-lg max-h-[90vh] overflow-y-auto p-4 sm:p-6" data-testid="company-record-dialog">
        <DialogHeader>
          <DialogTitle>{initial ? t("dash.crec.editTitle") : t("dash.crec.addTitle")}</DialogTitle>
          <DialogDescription>{t("dash.crec.dialogDesc")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden"
            onChange={onPickFile} data-testid="record-file-input" />
          <div className="rounded-xl border-2 border-dashed border-blue-200 bg-blue-50/50 p-3">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 text-sm text-blue-800">
                <Sparkles className="h-4 w-4" />
                <span>{t("dash.crec.aiHint")}</span>
              </div>
            </div>
            <div className="mt-2 flex items-center gap-2">
              <Button type="button" variant="outline" size="sm" disabled={extracting}
                onClick={() => fileRef.current?.click()} data-testid="record-upload-extract-btn"
                className="border-blue-300 text-blue-700 hover:bg-blue-100">
                {extracting ? <Loader2 className="h-4 w-4 ml-1 animate-spin" /> : <Paperclip className="h-4 w-4 ml-1" />}
                {extracting ? t("dash.crec.reading") : t("dash.crec.uploadExtract")}
              </Button>
              {form.attachment_id && (
                <div className="flex items-center gap-1 text-xs bg-white border rounded-full px-2 py-1" data-testid="record-attachment-chip">
                  <a href={fileUrl(form.attachment_id)} target="_blank" rel="noreferrer"
                    className="flex items-center gap-1 text-blue-700 hover:underline max-w-[160px] truncate">
                    <ExternalLink className="h-3 w-3 shrink-0" />
                    <span className="truncate">{form.attachment_name || t("dash.crec.attachFallback")}</span>
                  </a>
                  <button type="button" onClick={() => setForm((f) => ({ ...f, attachment_id: null, attachment_name: null }))}
                    data-testid="record-remove-attachment" className="text-slate-400 hover:text-red-500">
                    <X className="h-3 w-3" />
                  </button>
                </div>
              )}
            </div>
            {(form.cr_number || form.verify_url) && (
              <div className="mt-2 flex flex-wrap items-center gap-2" data-testid="record-qr-info">
                {form.cr_number && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-white border border-slate-200 px-2.5 py-1 text-xs text-slate-700">
                    {t("dash.crec.crNumber")} <span className="font-mono-nums font-semibold">{form.cr_number}</span>
                  </span>
                )}
                {form.verify_url && (
                  <a href={form.verify_url} target="_blank" rel="noreferrer" data-testid="record-verify-link"
                    className="inline-flex items-center gap-1 rounded-full bg-emerald-600 px-3 py-1 text-xs font-semibold text-white hover:bg-emerald-700 transition">
                    <ExternalLink className="h-3 w-3" /> {t("dash.crec.openVerify")}
                  </a>
                )}
              </div>
            )}
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">{t("dash.crec.type")}</label>
            <Select value={(form.category && !categories.includes(form.category) ? "أخرى" : form.category) || undefined} onValueChange={(v) => { set("category", v); if (["الإقامة", "كرت العمل"].includes(v) || isGosiCat(v)) { if (!form.name.trim()) set("name", v); } if (isGosiCat(v)) set("payment_period", "monthly"); }}>
              <SelectTrigger data-testid="record-category-trigger" className="mt-1">
                <SelectValue placeholder={t("dash.crec.pickType")} />
              </SelectTrigger>
              <SelectContent>
                {categories.map((c) => (
                  <SelectItem key={c} value={c} data-testid={`record-category-${c}`}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {form.category && (form.category === "أخرى" || !categories.includes(form.category)) && (
              <div className="mt-2">
                <Input data-testid="record-custom-category" list="crec-custom-cats" value={form.category === "أخرى" ? "" : form.category}
                  onChange={(e) => set("category", e.target.value.trim() ? e.target.value : "أخرى")} placeholder="اكتب اسم التصنيف بدل «أخرى» (مثال: رسوم حكومية، اشتراكات)" />
                <datalist id="crec-custom-cats">{used.map((c) => <option key={c} value={c} />)}</datalist>
              </div>
            )}
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">{t("dash.crec.nameLabel")}</label>
            <Input data-testid="record-name-input" className="mt-1" value={form.name}
              onChange={(e) => set("name", e.target.value)} placeholder={t("dash.crec.namePh")} />
            <NameHint name={form.name} category={form.category} saved={!!initial} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-slate-700">{t("dash.crec.expiry")}</label>
              <Input data-testid="record-expiry-input" type="date" className="mt-1" value={form.expiry_date}
                readOnly={kindOf(form.name, form.category) === "كرت العمل"}
                onChange={(e) => set("expiry_date", e.target.value)} />
              {kindOf(form.name, form.category) === "كرت العمل" && <p className="mt-1 text-[11px] text-blue-700" data-testid="record-expiry-linked">مرتبط تلقائياً بتاريخ انتهاء الإقامة</p>}
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">{t("dash.crec.lastAmount")}</label>
              <Input data-testid="record-amount-input" type="number" min="0" step="0.01" className="mt-1"
                value={form.last_payment_amount} onChange={(e) => set("last_payment_amount", e.target.value)} placeholder="0.00" />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">المبلغ يُدفع كل</label>
            <Select value={form.payment_period} onValueChange={(v) => set("payment_period", v)}>
              <SelectTrigger className="mt-1" data-testid="record-payment-period-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(PERIODS).map(([k, v]) => <SelectItem key={k} value={k} data-testid={`record-period-${k}`}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
            {Number(form.last_payment_amount) > 0 && <p className="mt-1 text-xs text-slate-500" data-testid="record-monthly-share">نصيب الشهر في قائمة الأرباح والخسائر: <b className="font-mono-nums">{(Number(form.last_payment_amount) * FACTOR[form.payment_period] / 12).toLocaleString("en", { maximumFractionDigits: 2 })}</b></p>}
          </div>
          {initial && kindOf(form.name, form.category) && (
            <WorkPermitsBox initial={initial} category={form.category} onSynced={(r) => { set("last_payment_amount", r.amount); if (r.expiry_date) set("expiry_date", r.expiry_date); onSaved(); }} />
          )}
          <div>
            <label className="text-sm font-medium text-slate-700">{t("dash.crec.lastDate")}</label>
            <Input data-testid="record-payment-date-input" type="date" className="mt-1" value={form.last_payment_date}
              onChange={(e) => set("last_payment_date", e.target.value)} />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">{t("dash.crec.notes")}</label>
            <Textarea data-testid="record-notes-input" className="mt-1" rows={2} value={form.notes}
              onChange={(e) => set("notes", e.target.value)} placeholder={t("dash.crec.notesPh")} />
          </div>
        </div>
        <DialogFooter className="sticky -bottom-4 sm:-bottom-6 z-10 gap-2 border-t border-slate-100 bg-white py-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="record-cancel-btn">{t("dash.common.cancel")}</Button>
          <Button onClick={submit} disabled={saving} data-testid="record-save-btn"
            className="bg-blue-600 hover:bg-blue-700">{saving ? "..." : t("dash.common.save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const LockerSection = ({ canEdit }) => {
  const [lockers, setLockers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [del, setDel] = useState(null);

  const load = async () => {
    try { const d = await api.listLockers(); setLockers(d.lockers || []); } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const openAdd = () => { setEditing(null); setName(""); setUrl(""); setOpen(true); };
  const openEdit = (l) => { setEditing(l); setName(l.name); setUrl(l.url); setOpen(true); };
  const save = async () => {
    if (!name.trim() || !url.trim()) { toast.error("أدخل الاسم والرابط"); return; }
    setBusy(true);
    try {
      if (editing) await api.updateLocker(editing.id, name.trim(), url.trim());
      else await api.createLocker(name.trim(), url.trim());
      toast.success(editing ? "تم تحديث اللوكر" : "تمت إضافة اللوكر");
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحفظ"); }
    finally { setBusy(false); }
  };
  const doDelete = async () => {
    if (!del) return;
    try { await api.deleteLocker(del.id); toast.success("تم الحذف"); setDel(null); load(); }
    catch { toast.error("تعذّر الحذف"); }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="company-lockers-section">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-2 font-heading font-bold text-slate-800">
          <Lock className="h-5 w-5 text-indigo-600" /> اللوكر
        </div>
        {canEdit && (
          <Button data-testid="add-locker-btn" size="sm" onClick={openAdd} className="gap-1.5 bg-indigo-600 hover:bg-indigo-700">
            <Plus className="h-4 w-4" /> إضافة لوكر
          </Button>
        )}
      </div>
      {lockers.length === 0 ? (
        <p className="text-sm text-slate-400 p-6 text-center">لا يوجد لوكر بعد. أضف رابطًا لينقلك مباشرة للموقع.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-3">
          {lockers.map((l) => (
            <div key={l.id} data-testid={`locker-row-${l.id}`} className="flex items-center justify-between gap-2 rounded-xl border border-slate-200 p-3 hover:border-indigo-300 transition">
              <a href={l.url} target="_blank" rel="noopener noreferrer" data-testid={`locker-open-${l.id}`}
                className="flex items-center gap-2 min-w-0 text-indigo-700 hover:underline">
                <ExternalLink className="h-4 w-4 shrink-0" />
                <span className="font-semibold text-sm truncate">{l.name}</span>
              </a>
              {canEdit && (
                <div className="flex items-center gap-1 shrink-0">
                  <Button data-testid={`edit-locker-${l.id}`} variant="ghost" size="icon" className="h-8 w-8 text-slate-500" onClick={() => openEdit(l)}><Pencil className="h-4 w-4" /></Button>
                  <Button data-testid={`del-locker-${l.id}`} variant="ghost" size="icon" className="h-8 w-8 text-rose-500" onClick={() => setDel(l)}><Trash2 className="h-4 w-4" /></Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="locker-dialog">
          <DialogHeader><DialogTitle>{editing ? "تعديل لوكر" : "إضافة لوكر"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-slate-700">الاسم</label>
              <Input data-testid="locker-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="مثال: لوكر الرياض" className="mt-1" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">الرابط</label>
              <Input data-testid="locker-url-input" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…" dir="ltr" className="mt-1" />
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="locker-save-btn" onClick={save} disabled={busy} className="gap-1.5">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} حفظ
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={Boolean(del)} onOpenChange={(v) => !v && setDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>تأكيد الحذف</AlertDialogTitle>
            <AlertDialogDescription>هل تريد حذف «{del?.name}»؟</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>إلغاء</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-locker" onClick={doDelete} className="bg-rose-600 hover:bg-rose-700">حذف</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const TIP_KEY = "crec_autocalc_tip_v1";
const AutoCalcTip = () => {
  const [show, setShow] = useState(() => !localStorage.getItem(TIP_KEY));
  if (!show) return null;
  return (
    <div className="relative rounded-2xl border border-indigo-200 bg-indigo-50/70 p-4 pe-10 text-sm text-slate-700" data-testid="crec-autocalc-tip">
      <button className="absolute top-3 end-3 text-slate-400 hover:text-slate-700" onClick={() => { localStorage.setItem(TIP_KEY, "1"); setShow(false); }} data-testid="crec-autocalc-tip-close" aria-label="إغلاق"><X className="h-4 w-4" /></button>
      <p className="mb-1 flex items-center gap-1.5 font-bold text-indigo-800"><Sparkles className="h-4 w-4" /> ميزة جديدة: حساب تلقائي لتكلفة الإقامات ورخص العمل</p>
      <p>يحسب النظام المبلغ تلقائياً = <b>عدد المقيمين الحاليين × الرسوم السنوية</b>، ويتحدّث وحده عند إضافة موظف مقيم أو حذفه أو تحويله إلى «منتهي الخدمة».</p>
      <ul className="mt-2 list-disc space-y-1 ps-5">
        <li>عند إضافة سجل اختر النوع <b className="rounded bg-white px-1.5">الإقامة</b> أو <b className="rounded bg-white px-1.5">كرت العمل</b> من قائمة التصنيف — يتعرّف عليه النظام تلقائياً.</li>
        <li>تاريخ انتهاء «كرت العمل» مرتبط تلقائياً بتاريخ انتهاء «الإقامة» (أو أقرب إقامة تنتهي من موظفيك).</li>
        <li><b>«تأمينات اجتماعية سعوديين»</b> = 20% من مجموع الرواتب الأساسية للسعوديين، و<b>«تأمين مقيمين»</b> = 8% من رواتب المقيمين — شهرياً (النسبة قابلة للتعديل).</li>
        <li>احفظ السجل ثم افتحه بالتعديل، يظهر لك مربع «حساب تلقائي». اكتب رسوم المقيم (الافتراضي 650 للإقامة و9700 لكرت العمل) واضغط <b>«مطابقة»</b>.</li>
      </ul>
    </div>
  );
};

export const CompanyRecordsPanel = ({ canEdit }) => {
  const { t, dir } = useLang();
  const [records, setRecords] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [cost, setCost] = useState(null);
  const [catsOpen, setCatsOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [recs, cats] = await Promise.all([api.companyRecords(), api.companyRecordCategories()]);
      setRecords(recs);
      api.companyCostSummary().then(setCost).catch(() => {});
      setCategories(cats);
    } catch {
      toast.error(t("dash.crec.loadFail"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return records.filter((r) => {
      const q = search.trim().toLowerCase();
      const matchSearch = !q || (r.name || "").toLowerCase().includes(q) || (r.category || "").toLowerCase().includes(q);
      const matchStatus = statusFilter === "all" || r.status === statusFilter;
      return matchSearch && matchStatus;
    });
  }, [records, search, statusFilter]);

  const counts = useMemo(() => ({
    total: records.length,
    expired: records.filter((r) => r.status === "expired").length,
    expiring: records.filter((r) => r.status === "expiring_soon").length,
  }), [records]);

  const doDelete = async () => {
    if (!deleting) return;
    try {
      await api.deleteCompanyRecord(deleting.id);
      toast.success(t("dash.crec.deleted"));
      setDeleting(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("dash.common.deleteFail"));
    }
  };

  return (
    <div className="space-y-5" data-testid="company-records-panel" dir={dir}>
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Building2 className="h-5 w-5 text-blue-600" />
          <h2 className="font-heading text-lg font-bold text-slate-800">{t("dash.crec.title")}</h2>
        </div>
        {canEdit && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setCatsOpen(true)} data-testid="manage-categories-btn">إدارة التصنيفات</Button>
            <Button onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="add-company-record-btn"
              className="flex-1 bg-blue-600 hover:bg-blue-700">
              <Plus className="h-4 w-4 ml-1" /> {t("dash.crec.addBtn")}
            </Button>
          </div>
        )}
        <CategoriesManager open={catsOpen} onOpenChange={setCatsOpen} onChanged={load} />
      </div>

      <AutoCalcTip />

      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-2xl border bg-white p-4" data-testid="record-stat-total">
          <div className="flex items-center gap-2 text-slate-500 text-sm"><FileText className="h-4 w-4" /> {t("dash.crec.statTotal")}</div>
          <div className="mt-1 text-2xl font-bold text-slate-800">{counts.total}</div>
        </div>
        <div className="rounded-2xl border bg-amber-50/60 border-amber-200 p-4" data-testid="record-stat-expiring">
          <div className="flex items-center gap-2 text-amber-700 text-sm"><CalendarClock className="h-4 w-4" /> {t("dash.crec.statExpiring")}</div>
          <div className="mt-1 text-2xl font-bold text-amber-700">{counts.expiring}</div>
        </div>
        <div className="rounded-2xl border bg-red-50/60 border-red-200 p-4" data-testid="record-stat-expired">
          <div className="flex items-center gap-2 text-red-700 text-sm"><CalendarClock className="h-4 w-4" /> {t("dash.crec.statExpired")}</div>
          <div className="mt-1 text-2xl font-bold text-red-700">{counts.expired}</div>
        </div>
      </div>
      {cost && <CostBoxes cost={cost} />}
      {cost && <CostByCategory items={cost.by_category} />}
      <CategoryCostChart records={records} />
      <RenewalForecast />
      <MissingNationality onFixed={() => api.companyCostSummary().then(setCost).catch(() => {})} canEdit={canEdit} />

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input data-testid="record-search-input" className="pr-9" placeholder={t("dash.crec.searchPh")}
            value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="sm:w-52" data-testid="record-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("dash.crec.allStatuses")}</SelectItem>
            <SelectItem value="valid">{t("dash.status.valid")}</SelectItem>
            <SelectItem value="expiring_soon">{t("dash.status.expiring_soon")}</SelectItem>
            <SelectItem value="expired">{t("dash.status.expired")}</SelectItem>
            <SelectItem value="none">{t("dash.crec.noneStatus")}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-2xl border bg-white overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="text-right">{t("dash.crec.colName")}</TableHead>
              <TableHead className="text-right">{t("dash.crec.colExpiry")}</TableHead>
              <TableHead className="text-right">{t("dash.crec.colStatus")}</TableHead>
              <TableHead className="text-right">{t("dash.crec.colAmount")}</TableHead>
              <TableHead className="text-right">{t("dash.crec.colPayDate")}</TableHead>
              <TableHead className="text-right">{t("dash.crec.colAttach")}</TableHead>
              <TableHead className="text-right">{t("dash.crec.colNotes")}</TableHead>
              {canEdit && <TableHead className="text-right">{t("dash.crec.colActions")}</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8} className="text-center py-10 text-slate-400">{t("dash.loading")}</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="text-center py-10 text-slate-400" data-testid="records-empty">
                {t("dash.crec.empty")}
              </TableCell></TableRow>
            ) : filtered.map((r) => (
              <TableRow key={r.id} data-testid={`record-row-${r.id}`}>
                <TableCell>
                  <div className="font-medium text-slate-800">{r.name}</div>
                  {r.category && <div className="text-xs text-slate-400">{r.category}</div>}
                </TableCell>
                <TableCell className="font-mono-nums text-slate-700">{r.expiry_date || "—"}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <StatusIcon status={r.status} />
                    {r.expiry_date ? <Countdown doc={r} /> : <span className="text-xs text-slate-400">{t("dash.crec.noneStatus")}</span>}
                  </div>
                </TableCell>
                <TableCell className="font-mono-nums text-slate-700"><Money value={r.last_payment_amount} />{r.last_payment_amount > 0 && <div className="text-[10px] text-slate-400" data-testid={`record-period-label-${r.id}`}>/ {PERIODS[r.payment_period || "yearly"]}</div>}</TableCell>
                <TableCell className="font-mono-nums text-slate-500">{r.last_payment_date || "—"}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    {r.attachment_id ? (
                      <a href={fileUrl(r.attachment_id)} target="_blank" rel="noreferrer"
                        data-testid={`record-attachment-${r.id}`}
                        className="inline-flex items-center gap-1 text-blue-700 hover:underline text-sm">
                        <Paperclip className="h-3.5 w-3.5" /> {t("dash.crec.view")}
                      </a>
                    ) : <span className="text-slate-400">—</span>}
                    {r.verify_url && (
                      <a href={r.verify_url} target="_blank" rel="noreferrer"
                        data-testid={`record-verify-${r.id}`} title={t("dash.crec.verifyTitle")}
                        className="inline-flex items-center gap-1 text-emerald-700 hover:underline text-sm">
                        <ExternalLink className="h-3.5 w-3.5" /> {t("dash.crec.verify")}
                      </a>
                    )}
                  </div>
                </TableCell>
                <TableCell className="max-w-[180px] truncate text-slate-500 text-sm" title={r.notes}>{r.notes || "—"}</TableCell>
                {canEdit && (
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button size="icon" variant="ghost" className="h-8 w-8" data-testid={`edit-record-${r.id}`}
                        onClick={() => { setEditing(r); setDialogOpen(true); }}>
                        <Pencil className="h-4 w-4 text-slate-500" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8" data-testid={`delete-record-${r.id}`}
                        onClick={() => setDeleting(r)}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <RecordFormDialog open={dialogOpen} onOpenChange={setDialogOpen} onSaved={load}
        categories={categories} initial={editing} used={[...new Set(records.map((r) => r.category).filter((c) => c && !categories.includes(c)))]} />

      <AlertDialog open={Boolean(deleting)} onOpenChange={(v) => !v && setDeleting(null)}>
        <AlertDialogContent dir={dir}>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("dash.crec.delTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("dash.crec.delDesc", { name: deleting?.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel>{t("dash.common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-record">{t("dash.common.delete")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
